import "@/entry/index";
import { cn } from "@/lib/utils";
import type { WidgetConfig } from "@/types";
import type { WidgetInstance, WidgetClass } from "@/types";
import { useRef, useEffect, ComponentProps } from "react";
import { formatWidgetClassName } from "@/utils/formt-widget-name";

interface WidgetProps extends ComponentProps<"div"> {
  id: string;
  widgetIdentifier: string;
  config?: Partial<WidgetConfig>;
}

export function BaseWidget({
  id,
  widgetIdentifier,
  config = {},
  ...props
}: WidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetInstanceRef = useRef<WidgetInstance | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const className = formatWidgetClassName(widgetIdentifier);

    const WidgetClass = window[className] as WidgetClass;

    if (!WidgetClass) return;

    try {
      const widgetConfig: WidgetConfig = {
        widgetId: id,
        ...config,
      };

      const widgetInstance = WidgetClass.init(widgetConfig);
      widgetInstanceRef.current = widgetInstance;
    } catch (error) {
      console.error(`Error initializing widget ${widgetIdentifier}:`, error);
    }

    return () => {
      if (widgetInstanceRef.current) {
        widgetInstanceRef.current.destroy();
        widgetInstanceRef.current = null;
      }
    };
  }, [id, widgetIdentifier, config]);

  return (
    <div
      id={id}
      className={cn("w-full bg-gray-100 widget-container", props.className)}
      ref={containerRef}
      {...props}
    ></div>
  );
}
